import { useMemo } from "react";

import { useDeployment, useEvmBackend } from "../../context";

import type { Address } from "viem";

export interface KnownAddress {
  address: Address;
  label: string;
  type: "account" | "contract" | "token";
}

export function useKnownAddresses(): KnownAddress[] {
  const { deployment } = useDeployment();
  const { accounts } = useEvmBackend();

  return useMemo(() => {
    const list: KnownAddress[] = [];

    // Backend accounts
    for (const acct of accounts) {
      list.push({
        address: acct.address,
        label: acct.label,
        type: "account",
      });
    }

    if (!deployment) return list;

    // Tokens
    if (deployment.tokens) {
      const entries: [string, string][] = [
        [deployment.tokens.weth, "WETH"],
        [deployment.tokens.usdc, "USDC"],
        [deployment.tokens.usdt, "USDT"],
        [deployment.tokens.dai, "DAI"],
        [deployment.tokens.wbtc, "WBTC"],
      ];
      for (const [addr, label] of entries) {
        if (addr) {
          list.push({ address: addr as Address, label, type: "token" });
        }
      }
    }

    // Contracts
    const contracts: [string, string][] = [
      [deployment.foundation.weth9, "WETH9"],
      [deployment.foundation.permit2, "Permit2"],
      [deployment.v2.factory, "V2 Factory"],
      [deployment.v2.router02, "V2 Router"],
      [deployment.v3.factory, "V3 Factory"],
      [deployment.v3.router, "V3 Router"],
      [deployment.v3.nonfungiblePositionManager, "V3 NFT PM"],
      [deployment.v3.quoterV2, "V3 Quoter"],
      [deployment.v4.poolManager, "V4 PoolManager"],
      [deployment.v4.positionManager, "V4 PositionMgr"],
      [deployment.v4.stateView, "V4 StateView"],
      [deployment.universalRouter.router, "Universal Router"],
      [deployment.uniswapX.exclusiveDutchOrderReactor, "Dutch Reactor"],
      [deployment.erc8004.identityRegistry, "Identity Registry"],
      [deployment.erc8004.reputationRegistry, "Reputation Registry"],
    ];

    // Vault contracts
    if (deployment.vault) {
      contracts.push(
        [deployment.vault.vaultFactory, "Vault Factory"],
        [deployment.vault.feeModule, "Fee Module"],
        [deployment.vault.identityAdapter, "Identity Adapter"],
        [deployment.vault.vaultImplementation, "Vault Impl"],
      );
    }

    for (const [addr, label] of contracts) {
      if (addr) {
        list.push({ address: addr as Address, label, type: "contract" });
      }
    }

    return list;
  }, [accounts, deployment]);
}
